import { View } from 'react-native';
import { COLOR, RADIUS, SPACING } from 'constants/design-system';

import { BaseIcon } from './BaseIcon';
import { BasePressable } from './BasePressable';
import { BaseText } from './BaseText';
import type { ViewStyle } from 'react-native';
import type { BaseIconProps } from './BaseIcon';
import type { BasePressableProps } from './BasePressable';
import type { BaseTextProps } from './BaseText';

export type BaseButtonProps = Pick<
  BasePressableProps,
  'onPress' | 'hitSlop' | 'disabled' | 'style' | 'onLayout'
> &
  Pick<ViewStyle, 'backgroundColor'> &
  Pick<
    BaseTextProps,
    | 'color'
    | 'fontSize'
    | 'fontWeight'
    | 'textAlign'
    | 'numberOfLines'
    | 'ellipsizeMode'
  > & {
    title?: BaseTextProps['children'];
    startIcon?: BaseIconProps;
    endIcon?: BaseIconProps;
    alignment?: 'vertical' | 'horizontal';
    textStyle?: BaseTextProps['style'];
  };

export const BaseButton = (props: BaseButtonProps) => {
  const {
    style,
    onPress,
    title,
    disabled,
    backgroundColor,
    color,
    fontSize,
    fontWeight,
    hitSlop,
    textAlign,
    startIcon,
    endIcon,
    alignment,
    ellipsizeMode,
    numberOfLines,
    textStyle,
    onLayout,
  } = props;

  return (
    <BasePressable
      onLayout={onLayout}
      onPress={onPress}
      backgroundColor={backgroundColor ?? COLOR.theme.white}
      hitSlop={hitSlop}
      style={({ pressed, ...rest }) => [
        {
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: RADIUS.theme.md,
          padding: SPACING.theme[4],
        },
        disabled && {
          backgroundColor: '#E9ECEF',
        },
        typeof style === 'function' ? style({ pressed, ...rest }) : style,
      ]}
    >
      <View
        style={{
          flexDirection: alignment === 'vertical' ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: SPACING.theme[4],
          maxWidth: '100%',
        }}
      >
        {startIcon ? (
          <BaseIcon
            {...startIcon}
            size={startIcon.size ?? fontSize}
            color={startIcon.color ?? color}
            style={[
              {
                textAlign: 'center',
              },
              disabled && {
                color: '#ADB5BD',
              },
              startIcon.style,
            ]}
          />
        ) : null}
        {title ? (
          <BaseText
            numberOfLines={numberOfLines}
            color={color}
            ellipsizeMode={ellipsizeMode}
            fontSize={fontSize}
            fontWeight={fontWeight}
            textAlign={textAlign ?? 'center'}
            style={[
              // { flex: 1 },
              disabled && {
                color: '#ADB5BD',
              },
              textStyle,
            ]}
          >
            {title}
          </BaseText>
        ) : null}

        {endIcon ? (
          <BaseIcon
            {...endIcon}
            size={endIcon.size ?? fontSize}
            color={endIcon.color ?? color}
            style={[
              {
                textAlign: 'center',
              },
              disabled && {
                color: '#ADB5BD',
              },
              endIcon.style,
            ]}
          />
        ) : null}
      </View>
    </BasePressable>
  );
};
